'use client';

import { useState, useEffect } from 'react';
import { useTranslations } from 'next-intl';

interface ContractPopupProps {
  isOpen: boolean;
  onClose: () => void;
  contractType: string;
  locale: string;
}

export default function ContractPopup({ isOpen, onClose, contractType, locale }: ContractPopupProps) {
  const t = useTranslations('register');
  const [contractContent, setContractContent] = useState<string>('');
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (isOpen && contractType) {
      loadContract();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, contractType, locale]);

  const loadContract = async () => {
    setLoading(true);
    try {
      // Contract type'dan dosya adına eşleştirme
      const trFileMap: Record<string, string> = {
        'UYELIKVEKULLANIM': 'uyelik-ve-kullanim',
        'KVKKAYDINLATMAMETNI': 'kisisel-verilerin-korunmasi-aydinlatma-metni',
        'CEREZPOLITIKASI': 'cerez-politikasi'
      };

      const enFileMap: Record<string, string> = {
        'UYELIKVEKULLANIM': 'membership-and-usage-terms',
        'KVKKAYDINLATMAMETNI': 'personal-data-protection-clarification',
        'CEREZPOLITIKASI': 'cookie-policy'
      };

      const fileKey = locale === 'tr' 
        ? trFileMap[contractType] || contractType.toLowerCase()
        : enFileMap[contractType] || contractType.toLowerCase();
      
      const filePath = `/contracts/${fileKey}.html`;
      const response = await fetch(filePath);
      
      if (response.ok) {
        const html = await response.text();
        setContractContent(html);
      } else {
        if (process.env.NODE_ENV !== 'production') {
        console.error(`Sözleşme yüklenemedi: ${filePath} - Status: ${response.status}`);
        }
        setContractContent('');
      }
    } catch (error) {
      if (process.env.NODE_ENV !== 'production') {
      console.error('Sözleşme yükleme hatası:', error);
      }
      setContractContent('');
    } finally {
      setLoading(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg shadow-xl max-w-4xl w-full max-h-[90vh] flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-gray-200">
          <h2 className="text-xl font-semibold text-gray-900">
            {contractType === 'UYELIKVEKULLANIM' 
              ? (locale === 'tr' ? 'Üyelik ve Kullanım Koşulları' : 'Membership and Terms of Use')
              : contractType === 'KVKKAYDINLATMAMETNI'
              ? (locale === 'tr' ? 'Kişisel Verilerin Korunması Aydınlatma Metni' : 'Personal Data Protection Clarification Text')
              : contractType === 'CEREZPOLITIKASI'
              ? (locale === 'tr' ? 'Çerez Politikası' : 'Cookie Policy')
              : contractType
            }
          </h2>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 transition-colors"
          >
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M18 6L6 18" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
              <path d="M6 6L18 18" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
            </svg>
          </button>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-6">
          {loading ? (
            <div className="flex items-center justify-center py-8">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-red-600"></div>
              <span className="ml-3 text-gray-600">{t('loading')}</span>
            </div>
          ) : contractContent ? (
            <div 
              className="prose prose-sm max-w-none"
              dangerouslySetInnerHTML={{ __html: contractContent }}
            />
          ) : (
            <div className="text-center py-8">
              <p className="text-gray-500">
                {locale === 'tr' ? 'Sözleşme yüklenemedi.' : 'Contract could not be loaded.'}
              </p>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="flex items-center justify-end p-6 border-t border-gray-200">
          <button
            onClick={onClose}
            className="px-6 py-2 bg-red-600 text-white rounded-md hover:bg-red-700 transition-colors"
          >
            {locale === 'tr' ? 'Kapat' : 'Close'}
          </button>
        </div>
      </div>
    </div>
  );
}
